/**
 * @fileoverview Servicio para gestión del LocalStorage de la acción formativa
 * @description Maneja la persistencia de datos de acción formativa en el navegador
 */

import { ICreateCursoAccionFormativa } from '../types';

const CURSOS_STORAGE_KEY = 'cg-seguros-cursos-accion-formativa';
const ACCION_FORMATIVA_ID_KEY = 'cg-seguros-accion-formativa-id';

/**
 * Servicio para gestión del LocalStorage de la acción formativa
 * @description Solo almacena el ID de la acción formativa, no los datos completos
 */
export const accionFormativaStorage = {

  /**
   * Guarda los cursos de la acción formativa en LocalStorage
   * @param cursos - Array de cursos a guardar
   */
  saveCursos(cursos: ICreateCursoAccionFormativa[]): void {
    try {
      localStorage.setItem(CURSOS_STORAGE_KEY, JSON.stringify(cursos));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  },

  /**
   * Obtiene los cursos de la acción formativa desde LocalStorage
   * @returns Array de cursos o null si no existen
   */
  getCursos(): ICreateCursoAccionFormativa[] | null {
    try {
      const stored = localStorage.getItem(CURSOS_STORAGE_KEY);
      if (stored) {
        const cursos = JSON.parse(stored);
        // Convertir fechas de string a Date
        return cursos.map((curso: any) => ({
          ...curso,
          fechaInicio: curso.fechaInicio ? new Date(curso.fechaInicio) : null,
          fechaFin: curso.fechaFin ? new Date(curso.fechaFin) : null
        }));
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Limpia los datos de la acción formativa del LocalStorage
   */
  clearAccionFormativa(): void {
    try {
      localStorage.removeItem(CURSOS_STORAGE_KEY);
      localStorage.removeItem(ACCION_FORMATIVA_ID_KEY);
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  },

  /**
   * Verifica si existe una acción formativa guardada
   * @returns true si existe un ID de acción formativa guardado
   */
  hasAccionFormativa(): boolean {
    return this.getAccionFormativaId() !== null;
  },

  /**
   * Guarda el ID de la acción formativa en LocalStorage
   * @param id - ID de la acción formativa
   */
  saveAccionFormativaId(id: number): void {
    try {
      localStorage.setItem(ACCION_FORMATIVA_ID_KEY, JSON.stringify(id));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  },

  /**
   * Obtiene el ID de la acción formativa desde LocalStorage
   * @returns ID de la acción formativa o null si no existe
   */
  getAccionFormativaId(): number | null {
    try {
      const stored = localStorage.getItem(ACCION_FORMATIVA_ID_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Limpia el ID de la acción formativa del LocalStorage
   */
  clearAccionFormativaId(): void {
    try {
      localStorage.removeItem(ACCION_FORMATIVA_ID_KEY);
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }
};

